import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { ModuleKey } from '@/access/modules';

/**
 * לוח הבקרה של הפלטפורמה (מיגרציה 071): מה דולק, וכמה AI מותר.
 */
export type AiQuota = {
  module_key: string;
  is_active: boolean;
  monthly_limit: number;
  used: number;
  left: number;
  note: string | null;
};

/** שורה בעץ המודולים (מיגרציה 075). */
export type FeatureNode = {
  key: ModuleKey;
  parent_key: ModuleKey | null;
  label: string;
  /** מה שנשמר בפועל: true, false, או null כשמעולם לא נגעו בו. */
  saved: boolean | null;
  /** מה שיוצא אחרי ירושה מההורה. זה מה שהעובד רואה. */
  effective: boolean;
};

export type PlatformBoard = {
  features: FeatureNode[];
  modules: Partial<Record<ModuleKey, boolean>>;
  budgets: AiQuota[];
};

const KEY = ['platform'] as const;

export function usePlatformBoard() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<PlatformBoard | null> => {
      const { data, error } = await supabase.rpc('platform_board');
      if (error) throw error;
      return (data as unknown as PlatformBoard | null) ?? null;
    },
  });
}

export function usePlatformMutations() {
  const queryClient = useQueryClient();

  /**
   * אחרי שינוי מודול צריך לרענן גם את me ואת ההגדרות: התפריט
   * והמסכים נגזרים מהם, ובלי זה הפיצ'ר נדלק רק אחרי רענון.
   */
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: KEY });
    void queryClient.invalidateQueries({ queryKey: ['me'] });
    void queryClient.invalidateQueries({ queryKey: ['settings'] });
  };

  const setModule = useMutation({
    mutationFn: async (input: { key: ModuleKey; enabled: boolean }) => {
      const { error } = await supabase.rpc('set_modules', {
        p_modules: { [input.key]: input.enabled },
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const setBudget = useMutation({
    mutationFn: async (input: {
      module: string;
      limit: number;
      active: boolean;
      note?: string | null;
    }) => {
      const { error } = await supabase.rpc('set_ai_budget', {
        p_module: input.module,
        p_limit: input.limit,
        p_active: input.active,
        p_note: input.note ?? undefined,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { setModule, setBudget };
}
